/**
 * Platform Admin — общие форматтеры для KPI / таблиц.
 *
 * Локаль ru-RU зашита: интерфейс полностью русскоязычный,
 * Platform Admin не выходит на международного клиента.
 */
import type { TenantStatus, TenantTariff } from './__mocks__/platform-admin';

const numFmt = new Intl.NumberFormat('ru-RU');
const dateFmt = new Intl.DateTimeFormat('ru-RU', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function formatNumber(n: number): string {
  return numFmt.format(n);
}

export function formatHectares(n: number): string {
  return `${numFmt.format(Math.round(n))} га`;
}

export function formatRub(n: number): string {
  // Compact формат "1 248 000 ₽".
  return `${numFmt.format(Math.round(n))} ₽`;
}

/**
 * «Платно до». `null` = платформа срока не знает (живой `/platform/companies`
 * биллинг не отдаёт) — показываем прочерк, а не «Бессрочный»: бессрочность
 * читается из колонки «Тариф», а выдуманная дата дороже пустой ячейки.
 */
export function formatPaidUntil(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return dateFmt.format(new Date(iso));
  } catch {
    return iso;
  }
}

// Форматтеры приростов (`+3 за 30д`, `+18%`) удалены вместе с фикстурами
// KPI (AK-470): сервер приростов не считает, а функция «отформатируй
// прирост» без источника данных — приглашение снова что-нибудь сочинить.

// ---------- Labels ----------

const STATUS_LABEL: Record<TenantStatus, string> = {
  active: 'active',
  trial: 'trial',
  paused: 'paused',
  archived: 'archived',
  suspended: 'suspended',
};

const TARIFF_LABEL: Record<TenantTariff, string> = {
  free: 'Free',
  trial: 'Trial',
  pro: 'Pro',
  enterprise: 'Enterprise',
  lifetime: 'Бессрочный',
};

export function statusLabel(s: TenantStatus): string {
  return STATUS_LABEL[s];
}

export function tariffLabel(t: TenantTariff | null | undefined): string {
  return t ? TARIFF_LABEL[t] : '—';
}

/**
 * Status → Badge variant mapping.
 * - active → primary (green; design-token bg-primary/15 text-primary)
 * - trial → accent-brand (amber)
 * - paused / suspended → secondary (grey)
 * - archived → outline (грейтекст без заливки)
 */
export function statusBadgeVariant(
  s: TenantStatus,
): 'primary' | 'accent-brand' | 'secondary' | 'outline' | 'destructive' {
  switch (s) {
    case 'active':
      return 'primary';
    case 'trial':
      return 'accent-brand';
    case 'paused':
      return 'secondary';
    case 'archived':
      return 'outline';
    case 'suspended':
      return 'destructive';
  }
}

export function tariffBadgeVariant(
  t: TenantTariff | null | undefined,
): 'primary' | 'accent-brand' | 'secondary' | 'outline' {
  switch (t) {
    case null:
    case undefined:
      return 'outline';
    case 'pro':
      return 'primary';
    case 'enterprise':
      return 'accent-brand';
    case 'trial':
      return 'secondary';
    case 'free':
      return 'outline';
    case 'lifetime':
      return 'accent-brand';
  }
}
